import { Container, Graphics } from "pixi.js";
import { GlowFilter } from "pixi-filters";

const DEFAULT_GLOW = {
    distance: 14,
    outerStrength: 2.5,
    innerStrength: 0.5,
    quality: 0.2,
};

export class PaylinesContainer extends Container {
    constructor({
        paylines,
        colors,
        columns,
        symbolWidth,
        symbolHeight,
        fieldWidth,
        lineWidth = 6,
        edgePadding = 20,
        cornerRadius = 18,
        clip = null,
        glow = true,
    }) {
        super();

        const glowOpts = glow ? { ...DEFAULT_GLOW, ...(typeof glow === "object" ? glow : {}) } : null;
        const centerX = (col) => columns[col].x + symbolWidth / 2;
        const centerY = (row) => row * symbolHeight + symbolHeight / 2;
        const rows = Math.max(...paylines.flatMap(positions => positions.map(([, row]) => row))) + 1;
        const maskWidth = fieldWidth + edgePadding * 2;
        const maskHeight = (clip?.height ?? rows * symbolHeight) + edgePadding * 2;
        const maskX = -edgePadding;
        const maskY = -edgePadding;

        this.lineEntries = paylines.map((positions, index) => {
            const firstRow = positions[0][1];
            const lastRow = positions.at(-1)[1];
            const color = colors[index] ?? 0xffffff;

            const pts = [];
            pts.push({ x: -edgePadding, y: centerY(firstRow) });
            for (const [col, row] of positions) {
                pts.push({ x: centerX(col), y: centerY(row) });
            }
            pts.push({ x: fieldWidth + edgePadding, y: centerY(lastRow) });

            const g = new Graphics();
            this.#tracePath(g, pts, cornerRadius);
            g.stroke({ width: lineWidth, color, alpha: 1, cap: "round", join: "round" });
            g.visible = false;

            if (glowOpts) {
                g.filters = [new GlowFilter({ ...glowOpts, color })];
            }

            const revealMask = new Graphics()
                .rect(0, 0, maskWidth, maskHeight)
                .fill(0xffffff);
            revealMask.x = maskX;
            revealMask.y = maskY;
            revealMask.scale.x = 0;

            const wrapper = new Container();
            wrapper.visible = false;
            wrapper.addChild(g);
            wrapper.addChild(revealMask);
            wrapper.mask = revealMask;

            this.addChild(wrapper);
            return { wrapper, line: g, revealMask };
        });
        this.lines = this.lineEntries.map(({ line }) => line);

        if (clip) {
            const maskGfx = new Graphics()
                .rect(clip.x, clip.y, clip.width, clip.height)
                .fill(0xffffff);
            this.addChild(maskGfx);
            this.mask = maskGfx;
        }
    }

    #tracePath(g, pts, radius) {
        if (pts.length < 2) return;
        if (radius <= 0 || pts.length === 2) {
            g.moveTo(pts[0].x, pts[0].y);
            for (let i = 1; i < pts.length; i++) g.lineTo(pts[i].x, pts[i].y);
            return;
        }
        g.moveTo(pts[0].x, pts[0].y);
        for (let i = 1; i < pts.length - 1; i++) {
            const r = Math.min(
                radius,
                this.#dist(pts[i - 1], pts[i]) / 2,
                this.#dist(pts[i], pts[i + 1]) / 2,
            );
            g.arcTo(pts[i].x, pts[i].y, pts[i + 1].x, pts[i + 1].y, r);
        }
        const last = pts[pts.length - 1];
        g.lineTo(last.x, last.y);
    }

    #dist(a, b) {
        return Math.hypot(a.x - b.x, a.y - b.y);
    }

    /**
     * Shows a payline and resets its reveal mask.
     *
     * @param {number} lineIndex - Payline index.
     */
    show(lineIndex) {
        const entry = this.lineEntries[lineIndex];
        if (!entry) return;

        entry.wrapper.visible = true;
        entry.line.visible = true;
        this.reveal(lineIndex, 0);
    }

    /**
     * Hides a payline and resets its reveal mask.
     *
     * @param {number} lineIndex - Payline index.
     */
    hide(lineIndex) {
        const entry = this.lineEntries[lineIndex];
        if (!entry) return;

        entry.wrapper.visible = false;
        entry.line.visible = false;
        this.reveal(lineIndex, 0);
    }

    /**
     * Hides all paylines.
     */
    hideAll() {
        for (let i = 0; i < this.lineEntries.length; i++) this.hide(i);
    }

    /**
     * Returns the drawable line graphics for animation effects.
     *
     * @param {number} lineIndex - Payline index.
     * @returns {Graphics|undefined}
     */
    getLine(lineIndex) {
        return this.lines[lineIndex];
    }

    /**
     * Returns the reveal mask used by a payline.
     *
     * @param {number} lineIndex - Payline index.
     * @returns {Graphics|null}
     */
    getRevealMask(lineIndex) {
        return this.lineEntries[lineIndex]?.revealMask ?? null;
    }

    /**
     * Sets payline reveal progress from left to right.
     *
     * @param {number} lineIndex - Payline index.
     * @param {number} progress - Reveal progress from 0 to 1.
     */
    reveal(lineIndex, progress) {
        const mask = this.getRevealMask(lineIndex);
        if (!mask) return;

        mask.scale.x = Math.max(0, Math.min(1, progress));
    }

    /**
     * Fully reveals a payline.
     *
     * @param {number} lineIndex - Payline index.
     */
    completeReveal(lineIndex) {
        this.reveal(lineIndex, 1);
    }
}
